import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateNudge } from "~/api/queries";
import { QuickWhenPicker } from "~/features/automation/QuickWhenPicker";
import { useNudgePicker } from "~/features/nudges/useNudgePicker";

/**
 * Mounts the QuickWhen picker wherever a nudge can be asked for. W2 built the picker; W7 is
 * the caller — the three triggers are the ✨ selection menu, the document ⋯ menu and the v2
 * BubbleBar's ⏰.
 *
 * The picker closes BEFORE the network call, so a slow server never leaves a popover hanging
 * over the document while somebody waits to find out whether it worked.
 *
 * ⚠ `channels.email` is never sent true. `NudgeJob` honours a true value, so the client's
 * `false` is the only thing between a reminder and somebody's mailbox (§A3.3) — the adapter
 * defaults it, FakeApi refuses it, and nothing here overrides either.
 */
export function NudgePickerHost() {
  const { t } = useTranslation();
  const request = useNudgePicker((state) => state.request);
  const closePicker = useNudgePicker((state) => state.closePicker);
  const createNudge = useCreateNudge();

  if (!request) {
    return null;
  }

  return (
    <QuickWhenPicker
      anchor={request.anchorRect}
      defaultNote={request.defaultNote}
      dueDate={request.dueDate}
      onClose={closePicker}
      onSet={(when) => {
        closePicker();
        createNudge.mutate(
          { target: request.target, when },
          {
            onSuccess: () => toast.success(t("Nudge set")),
            onError: (error) =>
              toast.error(
                error instanceof Error && error.message
                  ? error.message
                  : t("Couldn't set the nudge"),
              ),
          },
        );
      }}
    />
  );
}
